import React, { useState, useEffect } from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Divider,
  Tooltip,
  useMediaQuery,
  useTheme,
  styled,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Favorite as FavoriteIcon,
  BarChart as BarChartIcon,
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  Logout as LogoutIcon,
  Notifications as NotificationsIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
  AccountBalanceWallet,
} from "@mui/icons-material";

import { useNavigate, useLocation } from "react-router-dom";

const drawerWidth = 220;
const collapsedWidth = 70;

const StyledListItem = styled(ListItem)(({ theme, selected }) => ({
  borderRadius: 12,
  margin: "4px 8px",
  padding: "8px 12px",
  transition: "all 0.3s ease",
  backgroundColor: selected ? `${theme.palette.primary.main}20` : "transparent",
  "&:hover": {
    backgroundColor: selected
      ? `${theme.palette.primary.main}30`
      : theme.palette.action.hover,
  },
  "& .MuiListItemIcon-root": {
    color: selected ? theme.palette.primary.main : theme.palette.text.secondary,
    minWidth: "36px",
  },
  "& .MuiListItemText-primary": {
    fontWeight: selected ? 600 : 400,
    color: selected ? theme.palette.primary.main : theme.palette.text.primary,
  },
}));

const Sidebar = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [open, setOpen] = useState(!isMobile);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (isMobile) setOpen(false);
  }, [isMobile]);

  const toggleDrawer = () => {
    if (!isMobile) setOpen((prev) => !prev);
  };

  const menuItems = [
    { text: "Dashboard", icon: <DashboardIcon />, path: "/dashboard" },
    { text: "Users", icon: <PeopleIcon />, path: "/users" },
    {
      text: "Interested Matches",
      icon: <FavoriteIcon />,
      path: "/verify-profiles",
    },
    {
      text: "Notifications",
      icon: <NotificationsIcon />,
      path: "/notifications",
    },
    { text: "Reports", icon: <BarChartIcon />, path: "/reports" },
    {
      text: "User Payments",
      icon: <AccountBalanceWallet />,
      path: "/payment",
    },
  ];

  const handleLogoutClick = () => {
    setLogoutDialogOpen(true);
  };

  const handleLogoutConfirm = () => {
    localStorage.removeItem("isLoggedIn");
    localStorage.removeItem("loginData");
    setLogoutDialogOpen(false);
    navigate("/");
  };

  const handleLogoutCancel = () => {
    setLogoutDialogOpen(false);
  };

  return (
    <>
      <Drawer
        variant="permanent"
        sx={{
          width: open ? drawerWidth : collapsedWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: open ? drawerWidth : collapsedWidth,
            overflowX: "hidden",
            transition: theme.transitions.create("width", {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            borderRight: "none",
            boxShadow: theme.shadows[2],
            backgroundColor: theme.palette.background.paper,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: open ? "space-between" : "center",
            p: theme.spacing(1, 2),
            height: "64px",
          }}
        >
          {open && (
            <Typography
              variant="h6"
              noWrap
              sx={{
                fontWeight: 700,
                color: theme.palette.primary.main,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, #FF8E53 100%)`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Kayasth Vivah
            </Typography>
          )}
          <IconButton
            onClick={toggleDrawer}
            sx={{
              color: theme.palette.text.secondary,
              "&:hover": {
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            {open ? <ChevronLeftIcon /> : <MenuIcon />}
          </IconButton>
        </Box>

        <Divider sx={{ borderColor: theme.palette.divider }} />

        <List sx={{ p: 1 }}>
          {menuItems.map((item) => {
            const isSelected = location.pathname === item.path;
            return (
              <Tooltip
                key={item.text}
                title={!open ? item.text : ""}
                placement="right"
                arrow
              >
                <StyledListItem
                  button
                  onClick={() => navigate(item.path)}
                  selected={isSelected}
                >
                  <ListItemIcon>
                    {React.cloneElement(item.icon, {
                      color: isSelected ? "primary" : "inherit",
                    })}
                  </ListItemIcon>
                  {open && (
                    <ListItemText
                      primary={item.text}
                      primaryTypographyProps={{
                        variant: "body2",
                        sx: {
                          fontWeight: isSelected ? 600 : 400,
                        },
                      }}
                    />
                  )}
                </StyledListItem>
              </Tooltip>
            );
          })}
        </List>

        <Divider sx={{ borderColor: theme.palette.divider, mt: "auto" }} />

        <List sx={{ p: 1 }}>
          <Tooltip title={!open ? "Logout" : ""} placement="right" arrow>
            <StyledListItem button onClick={handleLogoutClick}>
              <ListItemIcon>
                <LogoutIcon color="inherit" />
              </ListItemIcon>
              {open && (
                <ListItemText
                  primary="Logout"
                  primaryTypographyProps={{ variant: "body2" }}
                />
              )}
            </StyledListItem>
          </Tooltip>
        </List>
      </Drawer>

      {/* Logout Confirmation Dialog */}
      <Dialog
        open={logoutDialogOpen}
        onClose={handleLogoutCancel}
        PaperProps={{
          sx: {
            borderRadius: 3,
            padding: 2,
            minWidth: "350px",
            background: theme.palette.background.paper,
          },
        }}
      >
        <DialogTitle
          sx={{ fontWeight: 600, color: theme.palette.text.primary }}
        >
          Confirm Logout
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: theme.palette.text.secondary }}>
            Are you sure you want to logout?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ padding: 2 }}>
          <Button
            onClick={handleLogoutCancel}
            variant="outlined"
            sx={{
              borderRadius: 2,
              textTransform: "none",
              px: 3,
              borderColor: theme.palette.divider,
              color: theme.palette.text.primary,
              "&:hover": {
                borderColor: theme.palette.divider,
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleLogoutConfirm}
            variant="contained"
            sx={{
              borderRadius: 2,
              textTransform: "none",
              px: 3,
              backgroundColor: theme.palette.primary.main,
              color: "#fff",
              "&:hover": {
                backgroundColor: theme.palette.primary.dark,
              },
            }}
          >
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Sidebar;
